/**
 * Theme Helper & Preset Catalog for Rhinos Front
 */

export const WALLPAPER_PRESETS = [
  {
    id: 'rhinos_gold_dark',
    name: 'Oro & Carbono Rhinos',
    cssImage:
      'radial-gradient(circle at 50% 15%, rgba(218, 165, 32, 0.4) 0%, rgba(30, 22, 6, 0.85) 55%, #050505 100%), linear-gradient(135deg, #1a1508 0%, #050505 100%)',
  },
  {
    id: 'amber_bronze_glow',
    name: 'Ámbar & Tierra Profunda',
    cssImage:
      'radial-gradient(circle at 80% 20%, rgba(255, 164, 0, 0.4) 0%, rgba(38, 22, 8, 0.85) 55%, #080503 100%), linear-gradient(135deg, #2b1807 0%, #0c0805 100%)',
  },
  {
    id: 'cyber_midnight',
    name: 'Medianoche Cyber',
    cssImage:
      'radial-gradient(circle at 20% 20%, rgba(0, 210, 255, 0.35) 0%, rgba(15, 25, 45, 0.85) 55%, #030712 100%), linear-gradient(135deg, #0b1528 0%, #030712 100%)',
  },
  {
    id: 'dark_mesh',
    name: 'Textura Carbón Minimal',
    cssImage:
      'repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.04) 0, rgba(255, 255, 255, 0.04) 2px, transparent 0, transparent 10px), linear-gradient(135deg, #1c1c1c 0%, #0d0d0d 100%)',
  },
];

export const PRESET_THEMES = {
  'rhinos-gold': {
    id: 'rhinos-gold',
    name: 'Rhinos Gold',
    description: 'Estilo clásico oficial Rhinos con dorado y negro profundo',
    colors: {
      primary: '#DAA520',
      primaryHover: '#B8860B',
      navBar: '#DAA520',
      navText: '#000000',
      drawerBg: '#212121',
      drawerText: '#FFFFFF',
      cardBg: 'rgba(33, 33, 33, 0.85)',
      background: '#000000',
      textPrimary: '#FFFFFF',
      textMuted: '#ABAAAA',
    },
    background: {
      mode: 'video', // 'video' | 'preset' | 'custom_url' | 'color'
      presetId: 'rhinos_gold_dark',
      customUrl: '',
      overlayOpacity: 0.3,
    },
  },
  'rhinos-amber': {
    id: 'rhinos-amber',
    name: 'Bronce & Ámbar',
    description: 'Tonos cálidos de marca inspirados en ámbar dorado y bronce',
    colors: {
      primary: '#FFA400',
      primaryHover: '#E08B00',
      navBar: '#FFA400',
      navText: '#000000',
      drawerBg: '#1A140E',
      drawerText: '#FFF8EE',
      cardBg: 'rgba(40, 28, 18, 0.85)',
      background: '#120E0A',
      textPrimary: '#FFF8EE',
      textMuted: '#C5B4A5',
    },
    background: {
      mode: 'preset',
      presetId: 'amber_bronze_glow',
      customUrl: '',
      overlayOpacity: 0.3,
    },
  },
  'rhinos-titanium': {
    id: 'rhinos-titanium',
    name: 'Titanio Cyber',
    description: 'Estilo nocturno contemporáneo con acentos cian y azul medianoche',
    colors: {
      primary: '#00D2FF',
      primaryHover: '#00B4D8',
      navBar: '#1E293B',
      navText: '#FFFFFF',
      drawerBg: '#0F172A',
      drawerText: '#F8FAFC',
      cardBg: 'rgba(24, 32, 47, 0.85)',
      background: '#0B0F17',
      textPrimary: '#F8FAFC',
      textMuted: '#94A3B8',
    },
    background: {
      mode: 'preset',
      presetId: 'cyber_midnight',
      customUrl: '',
      overlayOpacity: 0.3,
    },
  },
};

export const THEME_STORAGE_KEY = 'rhinos_user_theme';

export function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return '218, 165, 32';
  let c = hex.replace('#', '').trim();
  if (c.length === 3) {
    c = c.split('').map((char) => char + char).join('');
  }
  if (c.length === 6) {
    const num = parseInt(c, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `${r}, ${g}, ${b}`;
  }
  return '218, 165, 32';
}

/**
 * Apply theme properties to document :root CSS variables
 */
export function applyThemeToDOM(themeConfig) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!themeConfig || !themeConfig.colors) return;

  const colors = themeConfig.colors;
  const bg = themeConfig.background || {};

  root.style.setProperty('--rhinos-primary', colors.primary || '#DAA520');
  root.style.setProperty(
    '--rhinos-primary-hover',
    colors.primaryHover || colors.primary || '#B8860B'
  );
  root.style.setProperty('--rhinos-primary-rgb', hexToRgb(colors.primary));

  root.style.setProperty('--rhinos-nav-bg', colors.navBar || colors.primary || '#DAA520');
  root.style.setProperty('--rhinos-nav-text', colors.navText || '#000000');
  root.style.setProperty('--rhinos-drawer-bg', colors.drawerBg || '#212121');
  root.style.setProperty('--rhinos-drawer-text', colors.drawerText || '#FFFFFF');

  root.style.setProperty('--rhinos-card-bg', colors.cardBg || 'rgba(33, 33, 33, 0.85)');
  root.style.setProperty('--rhinos-background', colors.background || '#000000');
  root.style.setProperty('--rhinos-text-primary', colors.textPrimary || '#FFFFFF');
  root.style.setProperty('--rhinos-text-muted', colors.textMuted || '#ABAAAA');

  const opacity = typeof bg.overlayOpacity === 'number' ? bg.overlayOpacity : 0.3;
  root.style.setProperty('--rhinos-bg-overlay-opacity', `${opacity}`);
  root.style.setProperty('--rhinos-bg-overlay', `rgba(0, 0, 0, ${opacity})`);

  let bgImage = 'none';
  if (bg.mode === 'custom_url' && bg.customUrl) {
    bgImage = `linear-gradient(rgba(0, 0, 0, ${opacity}), rgba(0, 0, 0, ${opacity})), url("${bg.customUrl}")`;
  } else if (bg.mode === 'preset') {
    const presetId = bg.presetId || 'rhinos_gold_dark';
    const found = WALLPAPER_PRESETS.find((p) => p.id === presetId) || WALLPAPER_PRESETS[0];
    bgImage = `linear-gradient(rgba(0, 0, 0, ${opacity}), rgba(0, 0, 0, ${opacity})), ${found.cssImage}`;
  } else {
    bgImage = 'none';
  }
  root.style.setProperty('--rhinos-bg-image', bgImage);
}

/**
 * Safely retrieve stored theme with fallback
 */
export function getStoredTheme() {
  if (typeof localStorage === 'undefined') return PRESET_THEMES['rhinos-gold'];
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) return PRESET_THEMES['rhinos-gold'];
    const parsed = JSON.parse(raw);
    return parsed && parsed.colors ? parsed : PRESET_THEMES['rhinos-gold'];
  } catch (e) {
    console.warn('Failed to parse stored theme:', e);
    return PRESET_THEMES['rhinos-gold'];
  }
}

/**
 * Safely persist theme configuration to localStorage
 */
export function saveStoredTheme(themeConfig) {
  if (typeof localStorage === 'undefined' || !themeConfig) return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(themeConfig));
  } catch (e) {
    console.warn('Failed to save theme to localStorage:', e);
  }
}

/**
 * Intelligently extract theme configuration from various backend response formats,
 * including { ibo_info: { profiles: [ { custom_settings: { themeSettings: { ... } } } ] } }
 */
export function extractThemeConfig(data) {
  if (!data) return null;
  let parsed = data;
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data);
    } catch (e) {
      return null;
    }
  }

  if (parsed.colors) return parsed;

  if (parsed.custom_settings?.themeSettings?.colors) {
    return parsed.custom_settings.themeSettings;
  }

  if (Array.isArray(parsed.profiles) && parsed.profiles.length > 0) {
    const profile = parsed.profiles[0];
    if (profile?.custom_settings?.themeSettings?.colors) {
      return profile.custom_settings.themeSettings;
    }
    if (profile?.themeSettings?.colors) {
      return profile.themeSettings;
    }
    if (profile?.theme_settings?.colors) {
      return profile.theme_settings;
    }
  }

  const ibo = parsed.ibo_info || parsed.ibo;
  if (ibo) {
    return extractThemeConfig(ibo);
  }

  if (parsed.themeSettings?.colors) return parsed.themeSettings;
  if (parsed.theme_settings?.colors) return parsed.theme_settings;

  return null;
}
